import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ErrorNotice } from '../common/ErrorNotice';
import type { Airport, FlightType, Lounge, Terminal } from '@/constants/area';

import type { ReservationRestoreData } from '@/hooks/useRestoreReservation';
import { LoadingOverlay } from '@/components/elements/LoadingOverlay';
import { LoungeLogo } from '@/components/elements/LoungeLogo';
import { ROUTE_PATH } from '@/constants/routes';
import { SPACE_TYPE } from '@/constants/space';
import { useApiErrorTranslation } from '@/hooks/useApiErrorTranslation';
import { useApiRequest } from '@/hooks/useApiRequest';
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat';
import { useDialog } from '@/hooks/useDialog';
import { useFloorFormat } from '@/hooks/useFloorFormat';

// 予約確認画面で受け取るデータの型
interface ReservationConfirmData {
  serviceType: string;
  airport: Airport;
  flightType: FlightType;
  terminal: Terminal;
  lounge: Lounge;
  areaIds: number[];
  boardingDate: string;
  startDateTime?: string; // ISO8601形式
  endDateTime?: string; // ISO8601形式
  spaceId?: number; // API送信用（DBのID）
  spaceNumber?: number; // 表示用（ブース番号）
  userName?: string;
  loungeFloor?: string;
  key_type?: 'digital' | 'physical_key_only'; // キータイプ（パスコード用/ICカード用）
}

export const ReservationConfirmation = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { translateApiError } = useApiErrorTranslation();
  const [error, setError] = useState<{
    errorKey: string;
    detailKeys?: string[];
  } | null>(null);
  const {
    dialogRef: fullyBookedDialogRef,
    open: openFullyBookedDialog,
    close: closeFullyBookedDialog,
  } = useDialog();
  const { isRequesting, execute } = useApiRequest('POST', 'CreateReservation', {
    showGlobalLoading: false,
  });
  const { formatDateWithWeekday, formatTimeJst } = useDateTimeFormat();
  const { formatFloor } = useFloorFormat();

  const reservationData = location.state?.reservationData as
    | ReservationConfirmData
    | undefined;

  // 予約データがない場合は予約画面に戻す
  if (!reservationData) {
    navigate(ROUTE_PATH.RESERVATION);
    return null;
  }

  // 利用時間（分）を計算
  const calculateDuration = (start?: string, end?: string): number => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    return Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
  };

  const duration = calculateDuration(
    reservationData.startDateTime,
    reservationData.endDateTime,
  );

  const handleFullyBookedDialogConfirm = () => {
    closeFullyBookedDialog();
    // 復元データを渡して予約画面に戻る（日付は初期状態にリセット）
    const restoreData = {
      serviceType: reservationData.serviceType,
      airport: reservationData.airport,
      flightType: reservationData.flightType,
      terminal: reservationData.terminal,
      lounge: reservationData.lounge,
    };

    navigate(ROUTE_PATH.RESERVATION, {
      state: { restoreData },
    });
  };

  const handleConfirm = async () => {
    setError(null);

    try {
      if (!reservationData.startDateTime || !reservationData.endDateTime) {
        setError({
          errorKey: 'reservation.confirm.errors.timeNotSelected',
        });
        return;
      }

      const result = await execute({
        reservation: {
          start_time: reservationData.startDateTime,
          end_time: reservationData.endDateTime,
          area_ids: reservationData.areaIds, // 複数のエリアIDを配列で渡す
          space_type: reservationData.serviceType,
          space_id: reservationData.spaceId, // ブース予約時のスペース指定（DBのID）
        },
      });

      if (!result.ok) {
        const { isFullyBooked } = translateApiError(
          result.error,
          result.details,
        );

        if (isFullyBooked) {
          // 満枠の場合は専用ダイアログを表示
          openFullyBookedDialog();
        } else {
          setError({
            errorKey:
              result.error || 'reservation.confirm.errors.reservationFailed',
            detailKeys: result.details,
          });
        }
        return;
      }

      // 予約完了画面に遷移
      navigate(ROUTE_PATH.RESERVATION_COMPLETE, {
        state: {
          reservation: result.data.reservation,
        },
      });
    } catch (err) {
      // catchブロックで捕捉されるエラーも translateApiError で処理される
      setError({
        errorKey:
          err instanceof Error
            ? err.message
            : 'reservation.confirm.errors.reservationFailed',
      });
    }
  };

  return (
    <>
      {isRequesting && (
        <LoadingOverlay message={t('reservation.confirm.confirming')} />
      )}
      <div className="page p-auth page_b">
        <main className="page__main">
          <div className="page__content">
            <section className="l-section">
              <div className="l-section__line">
                <div className="l-container">
                  {/* ヒーロー */}
                  <div className="l-hero">
                    <img
                      className="l-hero__img"
                      src="/Common_Title.jpg"
                      alt="LOUNGE RESERVATION"
                    />
                  </div>

                  {/* エラー表示 */}
                  {error && (() => {
                    const { message, detailMessages } = translateApiError(error.errorKey, error.detailKeys);
                    return (
                      <ErrorNotice
                        message={message}
                        details={detailMessages}
                      />
                    );
                  })()}

                  {/* エリア群（グリッド） */}
                  <div className="l-grid">
                    {/* ボックス1：予約内容確認 */}
                    <section className="l-card">
                      <div className="l-card__body">
                        <h2 className="l-card__title">
                          {t('reservation.confirm.title')}
                        </h2>
                        <div className="l-card__confirm-note">
                          {t('reservation.confirm.note')}
                        </div>
                        <div
                          className={`l-box ${reservationData.serviceType === SPACE_TYPE.SHOWER_ROOM ? 'l-box--shower' : 'l-box--booth'} reservation-detail`}
                        >
                          <div className="reservation-detail__section reservation-detail__list">
                            {/* 氏名 */}
                            {reservationData.userName && (
                              <div className="reservation-detail__row">
                                <div className="reservation-detail__name">
                                  {t('reservation.confirm.namePrefix')}
                                  {reservationData.userName}
                                </div>
                              </div>
                            )}
                            {/* 日付 */}
                            <div className="reservation-detail__row">
                              <div className="reservation-detail__desc reservation-detail__date">
                                {reservationData.boardingDate &&
                                  formatDateWithWeekday(
                                    reservationData.boardingDate,
                                  )}
                              </div>
                            </div>
                            {/* 時間 */}
                            <div className="reservation-detail__row">
                              <div className="reservation-detail__desc reservation-detail__time">
                                {reservationData.startDateTime &&
                                  reservationData.endDateTime && (
                                    <>
                                      {formatTimeJst(
                                        reservationData.startDateTime,
                                      )}
                                      {t('reservation.confirm.timeRange')}
                                      {formatTimeJst(
                                        reservationData.endDateTime,
                                      )}
                                      {duration > 0 &&
                                        t(
                                          'reservation.confirm.durationFormat',
                                          {
                                            duration,
                                          },
                                        )}
                                    </>
                                  )}
                              </div>
                            </div>
                            {/* タイムゾーン注記 */}
                            <div className="reservation-detail__row">
                              <div className="reservation-card__info reservation-card__info--timezone-left">
                                {t('reservation.boarding.timezoneNote')}
                              </div>
                            </div>
                            {/* 空港 */}
                            <div className="reservation-detail__row">
                              <div className="reservation-detail__desc reservation-detail__airport">
                                {t(`common.airport.${reservationData.airport}`)}
                              </div>
                            </div>
                            {/* ターミナル */}
                            <div className="reservation-detail__row">
                              <div className="reservation-detail__desc reservation-detail__terminal">
                                {t('common.terminalWithFlightType', {
                                  terminal: t(
                                    `common.terminal.${reservationData.terminal}`,
                                  ),
                                  flightType: t(
                                    `common.flightType.${reservationData.flightType}`,
                                  ),
                                })}
                              </div>
                            </div>
                            {/* ラウンジ */}
                            <div className="reservation-detail__row reservation-detail__row--lounge">
                              <div className="reservation-detail__desc reservation-detail__lounge">
                                <div className="reservation-detail__lounge-inner">
                                  <LoungeLogo
                                    lounge={reservationData.lounge}
                                    className="reservation-detail__lounge-logo"
                                    altText={t(
                                      `common.lounge.${reservationData.lounge}`,
                                    )}
                                  />
                                  {reservationData.loungeFloor && (
                                    <div className="reservation-detail__lounge-floor">
                                      {formatFloor(reservationData.loungeFloor)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* 部屋（シャワールームまたは防音個室ブース） */}
                            <div className="reservation-detail__row reservation-detail__row--room">
                              <div className="reservation-detail__desc reservation-detail__room">
                                <div className="reservation-detail__room-inner">
                                  <div className="reservation-detail__room-img">
                                    <img
                                      className="reservation-detail__room-icon"
                                      src={
                                        reservationData.serviceType ===
                                        'shower_room'
                                          ? '/Common_Reservation_Shower.png'
                                          : '/Common_Reservation_Booth.png'
                                      }
                                      alt={
                                        reservationData.serviceType ===
                                        'shower_room'
                                          ? t('reservation.serviceType.shower')
                                          : t('reservation.serviceType.booth')
                                      }
                                    />
                                  </div>
                                  {reservationData.spaceNumber && (
                                    <div className="reservation-detail__room-no">
                                      No.
                                      <span className="reservation-detail__room-no-number">
                                        {reservationData.spaceNumber}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* ボックス2：注意事項 */}
                    <section className="l-card">
                      <div className="l-card__body">
                        <div
                          className={`l-box ${reservationData.serviceType === SPACE_TYPE.SHOWER_ROOM ? 'l-box--shower' : 'l-box--booth'} reservation-detail`}
                        >
                          <h2 className="reservation-detail___alert-title">
                            {t('reservation.confirm.beforeConfirm')}
                          </h2>

                          {/* ご利用上の注意 */}
                          <div className="reservation-detail__section reservation-detail__section--exit">
                            <div className="reservation-detail__section-title">
                              {t('reservation.confirm.usageNotes.title')}
                            </div>
                            {reservationData.serviceType ===
                            SPACE_TYPE.SHOWER_ROOM ? (
                              <>
                                <div className="reservation-detail__section-body">
                                  <div className="reservation-detail__text-row">
                                    <div className="reservation-detail__text-marker">
                                      ・
                                    </div>
                                    <div className="reservation-detail__text">
                                      {t(
                                        'reservation.confirm.usageNotes.duration',
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="reservation-detail__section-body">
                                  <div className="reservation-detail__text-row">
                                    <div className="reservation-detail__text-marker">
                                      ・
                                    </div>
                                    <div className="reservation-detail__text">
                                      {t(
                                        'reservation.confirm.usageNotes.noExtension',
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="reservation-detail__section-body">
                                  <div className="reservation-detail__text-row">
                                    <div className="reservation-detail__text-marker">
                                      ・
                                    </div>
                                    <div className="reservation-detail__text">
                                      {t(
                                        'reservation.confirm.usageNotes.autoCancel',
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="reservation-detail__section-body">
                                  <div className="reservation-detail__text-row">
                                    <div className="reservation-detail__text-marker">
                                      ・
                                    </div>
                                    <div className="reservation-detail__text">
                                      {t(
                                        'reservation.confirm.booth.usageNotes.noExtension',
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="reservation-detail__section-body">
                                  <div className="reservation-detail__text-row">
                                    <div className="reservation-detail__text-marker">
                                      ・
                                    </div>
                                    <div className="reservation-detail__text">
                                      {t(
                                        'reservation.confirm.booth.usageNotes.autoCancel',
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>

                          {/* 入室方法（防音ブースのみ） */}
                          {reservationData.serviceType ===
                            SPACE_TYPE.PRIVATE_ROOM && (
                            <div className="reservation-detail__section reservation-detail__section--exit">
                              <div className="reservation-detail__section-title">
                                {t('reservation.confirm.entry.title')}
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservation.confirm.booth.entry.timing',
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservation.confirm.booth.entry.unlock',
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* 退室方法（防音ブースのみ） */}
                          {reservationData.serviceType ===
                            SPACE_TYPE.PRIVATE_ROOM && (
                            <div className="reservation-detail__section reservation-detail__section--exit">
                              <div className="reservation-detail__section-title">
                                {t('reservation.confirm.exit.title')}
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t('reservation.confirm.booth.exit.method')}
                                  </div>
                                </div>
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservation.confirm.booth.exit.reminder',
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* アクションエリア */}
                        <div className="reservation-card__actions-item">
                          <div className="reservation-card__actions-item">
                            <button
                              onClick={handleConfirm}
                              disabled={isRequesting}
                              className="reservation-card__action-link reservation-card__action-link--confirmed"
                              type="button"
                            >
                              {isRequesting
                                ? t('reservation.confirm.confirming')
                                : t('reservation.confirm.confirmButton')}
                            </button>
                          </div>
                          <div className="reservation-card__actions-item">
                            <button
                              className="reservation-card__action-link reservation-card__action-link--back"
                              type="button"
                              onClick={() => {
                                const restoreData: ReservationRestoreData = {
                                  serviceType: reservationData.serviceType,
                                  airport: reservationData.airport,
                                  flightType: reservationData.flightType,
                                  terminal: reservationData.terminal,
                                  lounge: reservationData.lounge,
                                };

                                navigate(ROUTE_PATH.RESERVATION, {
                                  state: { restoreData },
                                });
                              }}
                              disabled={isRequesting}
                            >
                              {t('common.back')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
        {/* 満員ダイアログ */}
        <dialog className="dialog dialog--novacancy" ref={fullyBookedDialogRef}>
          <div className="dialog__line">
            <div className="dialog__area">
              <div className="dialog__text">
                <p className="dialog__text-line dialog__text-line-left">
                  {t('reservation.confirm.fullyBookedDialog.line1')}
                  <br />
                  {t('reservation.confirm.fullyBookedDialog.line2')}
                  <br />
                  {t('reservation.confirm.fullyBookedDialog.line3')}
                </p>
              </div>
              <div className="dialog__actions">
                <button
                  className="dialog__btn dialog__btn--ok js-bt-dialog"
                  type="button"
                  onClick={handleFullyBookedDialogConfirm}
                >
                  {t('reservation.confirm.fullyBookedDialog.okButton')}
                </button>
              </div>
            </div>
          </div>
        </dialog>
      </div>
    </>
  );
};
